import { autorun } from "mobx";
import { AttributeValueTypes } from "../enums";
import {
  IAPICheckOutPaymentModel,
  IAPICheckOutPurchaseItemsModel,
  IAPIPaymentIntentModel,
  IAPIPaymentReceiptViewModel,
  IAPIPurchaseCheckOutItemsModel,
} from "../interfaces";
import { ColorModel, ProductAttributeWithOption } from "./ProductModel";
import { PaymentMethodModel } from "./PaymentMethodModels";
import {
  CustomerFacingCheckOutItemModel,
  PurchaseCheckOutAddOnDetails,
} from "./PriceUtility";
import { UtilityMethods } from "@mindmemobile/mm-shared-global";

export class PurchaseCheckOutDetails {
  item: CustomerFacingCheckOutItemModel;
  addOns: PurchaseCheckOutAddOnDetails[];
  //-1 means no active variant is from selected item
  // value greater than -1 means active variant is from addOns
  activeItemIndex: number;
  constructor(data?: Partial<PurchaseCheckOutDetails>) {
    this.item = new CustomerFacingCheckOutItemModel(
      data?.item || new CustomerFacingCheckOutItemModel({})
    );
    this.addOns =
      data?.addOns?.map((i) => new PurchaseCheckOutAddOnDetails(i)) || [];
    if (data?.activeItemIndex === undefined) {
      this.activeItemIndex = -1;
    } else {
      this.activeItemIndex = data.activeItemIndex;
    }
    autorun(() => {
      this.validate(true);
    });
  }

  validate(isAutoRun = false) {
    this.item.validate(isAutoRun);
    this.addOns.forEach((addOn) => addOn.validate(isAutoRun));
    this.item = new CustomerFacingCheckOutItemModel(this.item);
    this.addOns = this.addOns.map((i) => new PurchaseCheckOutAddOnDetails(i));
  }

  createItemsAPIModel(): IAPICheckOutPurchaseItemsModel[] {
    const items: IAPICheckOutPurchaseItemsModel[] = [];
    items.push({
      itemId: this.item.itemId,
      itemType: this.item.itemType,
      quantity: this.item.quantity,
      hasVariants: this.item.hasVariants,
      variantId:
        this.item.hasVariants && this.item.selectedVariantIndex > -1
          ? this.item.variants?.options[this.item.selectedVariantIndex].id
          : "",
      priceId: this.item.price?.id,
      customAmount:
        this.item.itemType == "Donation"
          ? this.item.donationAmount
          : parseFloat(this.item.customPricingInputValue) > 0
            ? parseFloat(this.item.customPricingInputValue)
            : 0,
      //donationAmount || this.item.customPricingInputValue,
      isFreeIncluded: false,
      inMemoryOf: this.item.inMemoryOf,
      dedicatedTo: this.item.dedicatedTo,
      tributeMessage: this.item.tributeMessage,
      beneficiaries: this.item.beneficiaries,
      payMonthly: this.item.payMonthly,
      allowAnnualIncrease: this.item.allowAnnualIncrease,
      coverFee: this.item.coverFee,
      coverFeeMessage: this.item.coverFeeMessage,
      increasePercentage: this.item.annualIncreasePercentage,
      anonymousDonation: this.item.anonymousDonation,
    });
    this.item.includedItemsByCondition?.forEach((item) => {
      items.push({
        itemId: item.id,
        itemType: item.type,
        quantity: 1,
        hasVariants: item.hasVariants,
        variantId: "",
        priceId: "",
        customAmount: 0,
        isFreeIncluded: true,
      });
    });
    return items;
  }

  createAddOnsAPIModel(): IAPICheckOutPurchaseItemsModel[] | null {
    const items: IAPICheckOutPurchaseItemsModel[] = [];
    this.addOns?.forEach((addOn) => {
      items.push({
        itemId: addOn.itemId,
        itemType: addOn.itemType,
        quantity: addOn.isIncludedItem ? addOn.quantity + 1 : addOn.quantity,
        hasVariants: addOn.hasVariants,
        variantId: addOn.variantId,
        priceId: addOn.id,
        customAmount: 0,
        isFreeIncluded: addOn.isIncludedItem,
      });
    });
    return items.length > 0 ? items : null;
  }
}
export class PurchaseExtractedAttributeModel {
  variantId: string;
  attributeId: string;
  attributeName: string;
  attributeValueType: keyof typeof AttributeValueTypes;
  options: PurchaseExtractedAttributeOptionModel[];

  constructor(data?: Partial<PurchaseExtractedAttributeModel>) {
    this.attributeId = data?.attributeId || "";
    this.attributeName = data?.attributeName || "";
    this.options =
      data?.options?.map((i) => new PurchaseExtractedAttributeOptionModel(i)) ||
      [];
    this.attributeValueType = data?.attributeValueType || "Text";
    this.variantId = data?.variantId || "";
  }
}

export class PurchaseExtendedAttributeWithOptionModel extends ProductAttributeWithOption {
  variantId: string;
  constructor(data?: Partial<PurchaseExtendedAttributeWithOptionModel>) {
    super(data);
    this.variantId = data?.variantId || "";
  }
}
export class PurchaseExtractedAttributeOptionModel {
  optionId: string;
  optionName: string;
  color?: ColorModel;
  variantId: string;
  constructor(data?: Partial<PurchaseExtractedAttributeOptionModel>) {
    this.optionId = data?.optionId || "";
    this.optionName = data?.optionName || "";
    this.color = data?.color ? new ColorModel(data.color) : undefined;
    this.variantId = data?.variantId || "";
  }
}

export class PurchasePaymentIntentModel {
  clientSecret: string;
  gatewayId: string;
  paymentIntentId: string;
  allowedPaymentMethods: string[];
  constructor(data?: Partial<PurchasePaymentIntentModel>) {
    this.clientSecret = data?.clientSecret || "";
    this.gatewayId = data?.gatewayId || "";
    this.paymentIntentId = data?.paymentIntentId || "";
    this.allowedPaymentMethods = data?.allowedPaymentMethods || [];
  }

  static deserializeAPIModel(
    data: IAPIPaymentIntentModel
  ): PurchasePaymentIntentModel {
    const mappedData: Partial<PurchasePaymentIntentModel> = {
      clientSecret: data && data.clientSecret,
      gatewayId: data && data.gatewayId,
      paymentIntentId: data && data.paymentIntentId,
      allowedPaymentMethods: data && data.allowedPaymentMethods,
    };
    return new PurchasePaymentIntentModel(mappedData);
  }
  hasMultiplePaymentMethods() {
    return this.allowedPaymentMethods.length > 1;
  }

  createAPIModel(
    paymentMethodId: string,
    amount: number
  ): IAPICheckOutPaymentModel {
    const amountToPay = parseFloat(amount.toFixed(2));
    return {
      amount: amountToPay,
      paymentIntentId: this.paymentIntentId,
      paymentMethodId: paymentMethodId,
    };
  }
}

export class PurchaseCheckoutItemsDoneModel {
  paymentId: string;
  paymentDate: Date;
  paymentMethod: PaymentMethodModel;
  amountPaid: number;
  isDonation: boolean;
  donationFrequency: string;
  /**
   * The receipt exactly as the confirmation email rendered it, built by the pay API. Used by
   * "Download Receipt" so the PDF matches the emailed receipt. Null when the API returned none.
   */
  receipt: IAPIPaymentReceiptViewModel | null;
  constructor(data?: Partial<PurchaseCheckoutItemsDoneModel>) {
    this.paymentId = data?.paymentId || "";
    this.paymentDate = data?.paymentDate || new Date();
    this.paymentMethod = data?.paymentMethod || new PaymentMethodModel();
    this.amountPaid = data?.amountPaid || 0;
    this.isDonation = data?.isDonation || false;
    this.donationFrequency = data?.donationFrequency || "";
    this.receipt = data?.receipt || null;
  }

  static deserializeAPIModel(
    data: IAPIPurchaseCheckOutItemsModel,
    isDonation: boolean,
    donationFrequency: string
  ): PurchaseCheckoutItemsDoneModel {
    const mappedData: Partial<PurchaseCheckoutItemsDoneModel> = {
      paymentId: (data.paymentId ? `pay_${data.paymentId}` : "") || "",
      paymentDate: UtilityMethods.removeZFromDateString(data.paymentDate),
      paymentMethod:
        data.paymentMethod &&
        PaymentMethodModel.deserializeAPIModel(data.paymentMethod),
      amountPaid: data.amountPaid,
      isDonation: isDonation,
      donationFrequency: donationFrequency,
      receipt: data.receipt || null,
    };
    return new PurchaseCheckoutItemsDoneModel(mappedData);
  }
}
